import { StyledButtonGroup } from '@app/components/button-group/ButtonGroup';
import { StyledButton } from '@app/components/button/Button';
import { SelectField } from '@app/components/form-fields/SelectField';
import useAgents from '@app/hooks/agents/useAgents';
import useLocations from '@app/hooks/locations/useLocations';
import useModal from '@app/hooks/useModal';
import type { AINumber } from '@app/services/api.types';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Flex } from 'antd';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MdCheck } from 'react-icons/md';
import * as z from 'zod';
import { AgentAddModal } from './AgentAddModal';
import { TextField } from '@app/components/form-fields/TextField';

const assignAiSchema = z.object({
    location_id: z.string('Location is required').min(1, 'Select a location'),
    agent_id: z.string().optional(),
    number_id: z.string('Phone Number is required').min(1, 'Select a phone number'),
    failover_number: z.string('Failover number is required').min(1, 'Failover number is required'),
});

export type AssignAiFormValues = z.infer<typeof assignAiSchema>;

export type Option = { id: string; label: string; subLabel?: string };

export type AssignAiAgentFormProps = {
    id: string;
    locations?: Option[];
    locationId?: string;
    agents?: Option[];
    numbers?: Option[];
    initialValues?: Partial<AssignAiFormValues>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSubmit: any; //(values: AssignAiFormValues) => void;
    loading?: boolean;
    onReset?: () => void;
    data?: AINumber;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onCancel?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSave?: any;
};

export const AssignAiAgentForm: React.FC<AssignAiAgentFormProps> = ({
    initialValues,
    onSubmit,
    onCancel,
    data,
    ...props
}) => {
    const { data: locations, isLoading } = useLocations({
        per_page: 99999,
    });
    const { data: agents, isLoading: isAgentsLoading } = useAgents({
        per_page: 99999,
        page: 1,
    });

    const addAiAgentModal = useModal({});

    const {
        control,
        handleSubmit,
        reset,
        formState: { isValid, isSubmitting },
    } = useForm<AssignAiFormValues>({
        mode: 'all',
        resolver: zodResolver(assignAiSchema),
        defaultValues: {
            location_id: undefined,
            agent_id: undefined,
            number_id: undefined,
            failover_number: undefined,
            ...initialValues,
        },
    });

    useEffect(() => {
        if (data) {
            reset({
                location_id: data?.location_id || undefined,
                agent_id: data?.agent_id || undefined,
                number_id: data?.id || undefined,
                failover_number: data?.failover_number || undefined,
            });
        }
    }, [data, reset]);

    return (
        <form onSubmit={handleSubmit(onSubmit)} noValidate id={props.id}>
            <div className="form-fields-wrapper">
                <TextField
                    label="Phone Number"
                    placeholder="Phone Number"
                    value={data?.number}
                    disabled
                />

                <Controller
                    control={control}
                    name="location_id"
                    render={({ field, fieldState }) => (
                        <SelectField
                            fieldState={fieldState}
                            {...field}
                            allowClear={false}
                            loading={isLoading}
                            label="Select Location"
                            placeholder="Select a Location"
                            options={
                                locations?.data.flatMap((loc) => {
                                    const toOption = (item: typeof loc) => ({
                                        value: item.id.toString(),
                                        label: `${item.brand_name} - ${item.name}`,
                                    });
                                    const children = loc.children ?? [];
                                    return children.length
                                        ? children.map(toOption)
                                        : [toOption(loc)];
                                }) || []
                            }
                        />
                    )}
                />

                <Controller
                    control={control}
                    name="agent_id"
                    render={({ field, fieldState }) => (
                        <SelectField
                            fieldState={fieldState}
                            {...field}
                            loading={isAgentsLoading}
                            allowClear={false}
                            // label="AI Agent"
                            label={
                                <Flex justify="space-between" align="flex-end" gap={20}>
                                    <span>AI Agent</span>
                                    <a
                                        href="#"
                                        className="link"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            addAiAgentModal.open();
                                        }}>
                                        <small>Add New</small>
                                    </a>
                                </Flex>
                            }
                            placeholder="Select an AI Agent"
                            options={
                                agents?.data?.map((agent) => ({
                                    value: agent.id.toString(),
                                    label: agent.name,
                                })) || []
                            }
                        />
                    )}
                />

                <Controller
                    control={control}
                    name="failover_number"
                    render={({ field, fieldState }) => (
                        <TextField
                            fieldState={fieldState}
                            {...field}
                            label="Failover Number"
                            placeholder="Enter a Failover Number"
                        />
                    )}
                />

                <Alert
                    type="info"
                    description={
                        <span className="alert-info-message">
                            <b>Coming Soon:</b> Configure WhatsApp and other messaging channels for
                            this location.
                        </span>
                    }
                />
            </div>

            <div className="ant-modal-footer">
                <StyledButtonGroup justify="flex-end">
                    <StyledButton onClick={onCancel} color="black" data-testid="reset-button">
                        Cancel
                    </StyledButton>
                    <StyledButton
                        htmlType="submit"
                        type="primary"
                        disabled={!isValid}
                        loading={isSubmitting || props?.loading}
                        icon={<MdCheck />}
                        data-testid="register-Partner-button">
                        Save
                    </StyledButton>
                </StyledButtonGroup>
            </div>

            <AgentAddModal
                open={addAiAgentModal.isOpen}
                onCancel={() => addAiAgentModal.reset()}
                onOk={() => addAiAgentModal.reset()}
            />
        </form>
    );
};

export default AssignAiAgentForm;
